import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import { importCampaignStructure, triggerReverseSync } from '@/lib/apis';
import { campaignTaskTrackingQueryOptions } from '@/lib/query-options';
import type { CampaignData } from '@/lib/types';
import { toast } from '@/hooks/useToast';
import { useCampaignErrorToast } from '@/hooks/useCampaignErrorToast';

/**
 * Dispatch a reverse-sync (pull from Google Ads) and poll the task_tracking
 * summary until the matching task reaches a terminal state. Shows a toast on
 * completion. Cache invalidation of the affected queries is handled separately by
 * useReverseSyncInvalidation (mounted once in the campaign _slug layout), so it
 * fires exactly once and survives navigation away from this page (#5745).
 */
export function useReverseSync(campaign: CampaignData, options?: { silent?: boolean; importStructure?: boolean }) {
  const campaignId = campaign.id;
  // Background callers (auto-pull on open, #5566 Req 1) pass silent to suppress toasts. A second
  // foreground instance (the manual "Sync from Google Ads" button) stays non-silent and surfaces
  // completion to the user, so there is exactly one toast even with both instances mounted.
  const silent = options?.silent ?? false;
  // External campaigns (#6381): dispatch through the import endpoint instead —
  // same CAMPAIGN_REVERSE_SYNC task underneath, so all the polling, completion
  // detection and invalidation below apply unchanged.
  const importStructure = options?.importStructure ?? false;
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const campaignErrorToast = useCampaignErrorToast();
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(null);
  // Guards against firing the completion toast off a stale poll snapshot. The cleanup
  // effect can run on cached `in_progress=0, total>0` data from a previous task before
  // the post-dispatch refetch lands; require seeing in_progress>0 at least once first.
  const seenInProgressRef = useRef(false);

  const mutation = useMutation({
    mutationFn: () => (importStructure ? importCampaignStructure(campaignId) : triggerReverseSync(campaignId)),
    onMutate: () => {
      posthog?.capture('campaign_reverse_sync_attempted', {
        campaign_id: campaignId,
      });
    },
    onSuccess: (data) => {
      seenInProgressRef.current = false;
      setPendingTaskId(data.task_tracking_id);
      // Broad prefix invalidate: the query key is ['campaignTaskTracking', adCreation, campaignId];
      // a [..., campaignId] filter does not match because element-wise prefix matching
      // compares position-by-position and `campaignId !== adCreation`.
      void queryClient.invalidateQueries({ queryKey: ['campaignTaskTracking'] });
    },
    onError: (err) => {
      if (silent) return;
      campaignErrorToast({
        title: "Couldn't start sync",
        message: err instanceof Error ? err.message : undefined,
        campaign,
      });
    },
  });

  // Always poll task tracking — the query options stop polling when nothing is in
  // progress, so the cost when idle is one fetch per mount. Keeping it enabled lets
  // us detect a reverse-sync task that started in a previous mount (page reload,
  // navigation, tab switch) and resume showing pending UI + completion toast.
  const trackingQuery = useQuery({
    ...campaignTaskTrackingQueryOptions(campaignId, 'reverse-sync'),
  });

  // Sentinel id used when we resume polling for a task we did not dispatch ourselves
  // (the actual task_tracking_id is not surfaced by the summary endpoint).
  const RESUMED_TASK_SENTINEL = -1;

  // Resume polling on mount if the backend already has an in-flight reverse-sync task.
  useEffect(() => {
    if (pendingTaskId !== null) return;
    const summary = trackingQuery.data?.campaign_reverse_sync;
    if (summary && summary.in_progress > 0) {
      setPendingTaskId(RESUMED_TASK_SENTINEL);
    }
  }, [trackingQuery.data, pendingTaskId]);

  // Track whether we've ever seen the in-flight task in the poll. Without this the
  // cleanup effect can fire on a stale `in_progress=0, total>0` snapshot from before
  // the dispatch — the post-onSuccess invalidate hasn't refetched yet.
  useEffect(() => {
    if ((trackingQuery.data?.campaign_reverse_sync?.in_progress ?? 0) > 0) {
      seenInProgressRef.current = true;
    }
  }, [trackingQuery.data]);

  useEffect(() => {
    if (pendingTaskId === null || !trackingQuery.data) return;
    const summary = trackingQuery.data.campaign_reverse_sync;
    if (!summary) return;

    // Require total > 0 to avoid acting on a stale poll where the row is not yet visible.
    const isDone = summary.in_progress === 0 && summary.total > 0;
    if (!isDone) return;
    if (!seenInProgressRef.current) return;

    const failed = summary.failed > 0;
    if (failed) {
      if (!silent) {
        const apiMessage = summary.latest_error_message?.trim();
        const truncated = apiMessage && apiMessage.length > 250 ? `${apiMessage.slice(0, 250)}…` : apiMessage;
        campaignErrorToast({
          title: "Couldn't sync from Google Ads",
          message: truncated || 'Please try again.',
          campaign,
        });
      }
      posthog?.capture('campaign_reverse_sync_failed', {
        campaign_id: campaignId,
      });
    } else {
      if (!silent) {
        toast({
          description: 'Synced from Google Ads',
        });
      }
      posthog?.capture('campaign_reverse_sync_succeeded', {
        campaign_id: campaignId,
      });
    }
    setPendingTaskId(null);
    seenInProgressRef.current = false;
    // Cache invalidation is intentionally NOT done here. It lives in the durable
    // useReverseSyncInvalidation hook (mounted in the campaign _slug layout), which watches the
    // same task-tracking poller and fires once — so it survives navigation away from this page and
    // does not double-fire when both a silent auto-sync instance and the manual-button instance are
    // mounted on the campaign index (#5745).
  }, [pendingTaskId, trackingQuery.data, campaignId, posthog, silent]);

  return {
    dispatch: mutation.mutateAsync,
    isPending: pendingTaskId !== null || mutation.isPending,
  };
}
